/**
 * Rol del usuario — fuente unica para toda la aplicacion.
 *
 * EL FALLO QUE CIERRA (2026-08-02)
 * -------------------------------
 * El rol se guardaba SOLO en `sessionStorage`, y solo lo escribia la pantalla
 * de inicio de sesion (`pages/Login.js`) y el alta inicial
 * (`pages/InitialSetup.js`). El token, en cambio, vive en `localStorage`.
 *
 * `sessionStorage` es por pestaña; `localStorage` es por navegador. Resultado:
 * abrir la aplicacion en una pestaña nueva (o recargar tras restaurar sesion)
 * dejaba el token vivo y el rol vacio. Con `GET /auth/me` devolviendo 200 y
 * `role: "admin"`, la interfaz pintaba **0** botones de accion: nada de
 * "Lanzar campaña" en /bas, ni "Nueva regla" en Sigma/Yara. Un administrador
 * veia la aplicacion en modo lectura sin ningun aviso, y sin forma de saber
 * por que.
 *
 * EL ARREGLO
 * ----------
 * El rol se deriva de `GET /api/auth/me` al montar (lo hace `AuthContext`, que
 * ya llamaba a ese endpoint pero tiraba el rol a la basura) y se publica aqui.
 * `sessionStorage` se sigue escribiendo por compatibilidad con codigo antiguo,
 * pero ya NO es la fuente: es una cache.
 *
 * Las pantallas usan `useUserRole()` en vez de leer `sessionStorage` a pelo,
 * para volver a pintarse cuando el rol llega (llega despues del primer render,
 * porque es una peticion de red).
 */

import { useEffect, useState } from 'react';

const STORAGE_KEY = 'userRole';

/** Rol conocido: '' = no hay sesion / aun sin resolver. */
let currentRole = null; // null = todavia no resuelto en esta carga
let resolving = false; // hay una peticion a /auth/me en vuelo (o ya hecha)
const listeners = new Set();

const readStored = () => {
  try {
    return sessionStorage.getItem(STORAGE_KEY) || '';
  } catch {
    // Navegacion privada / almacenamiento bloqueado: no es motivo para romper.
    return '';
  }
};

const writeStored = (role) => {
  try {
    if (role) sessionStorage.setItem(STORAGE_KEY, role);
    else sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // silencio-ok: almacenamiento no disponible; el valor en memoria sigue valiendo
  }
};

/**
 * Publica el rol y despierta a todos los componentes suscritos.
 * La llama `AuthContext` en cuanto `/auth/me` responde.
 */
export const setUserRole = (role) => {
  const next = (role || '').toString();
  resolving = true;
  if (currentRole === next) return;
  currentRole = next;
  writeStored(next);
  listeners.forEach((notify) => notify(next));
};

/** Al cerrar sesion: sin rol y sin marca de "ya resuelto". */
export const clearUserRole = () => {
  currentRole = '';
  resolving = false;
  writeStored('');
  listeners.forEach((notify) => notify(''));
};

/** Lectura sincrona, para el codigo que no es un componente de React. */
export const getUserRole = () => (currentRole !== null ? currentRole : readStored());

/**
 * Hook: devuelve el rol del usuario, en minusculas y ya normalizado.
 *
 * Primer render: lo que haya en la cache de sesion (normalmente '' en pestaña
 * nueva). Cuando `AuthContext` resuelve `/auth/me`, el componente se vuelve a
 * pintar con el rol de verdad.
 */
export const useUserRole = () => {
  const [role, setRole] = useState(() => getUserRole());

  useEffect(() => {
    listeners.add(setRole);
    // Si otro componente ya lo resolvio antes de que este montara, ponerse al dia.
    const known = getUserRole();
    setRole(known);
    return () => {
      listeners.delete(setRole);
    };
  }, []);

  return (role || '').toLowerCase();
};

/** true si `AuthContext` ya publico un rol (o lo esta pidiendo). */
export const isUserRoleResolved = () => resolving || currentRole !== null;
